/**
 * CommissionLedgersPage — Issue #134
 *
 * 提成台账管理页面：
 *   - 列表展示（按 orderProjectId / staffId 过滤）
 *   - 核销提成（财务发放后 unlocked -> paid_off）
 *
 * 支持角色：渠道、市场人员、销售顾问、专家/助理
 * 权限门控：COMMISSION_LEDGER_READ / PAY_OFF
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import Decimal from 'decimal.js';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useCommissionLedgers,
  type CommissionLedgerResponseDto,
} from '@/hooks/useFinance';

/** Format a Decimal/string amount as CNY with 2 decimal places. */
function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

/** Format a ratio (0-1) as percentage string using Decimal. */
function fmtPct(ratio: string): string {
  return `${new Decimal(ratio).mul(100).toFixed(1)}%`;
}

const STATUS_LABELS: Record<string, string> = {
  pending_unlock: '待解锁',
  unlocked: '已解锁',
  paid_off: '已支付核销',
};

const ROLE_TYPE_LABELS: Record<string, string> = {
  channel: '渠道',
  market: '市场人员',
  sales_advisor: '销售顾问',
  expert: '专家',
  assistant: '助理',
};

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    pending_unlock: 'bg-yellow-100 text-yellow-800',
    unlocked: 'bg-blue-100 text-blue-800',
    paid_off: 'bg-green-100 text-green-800',
  };
  const cls = colorMap[status] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export default function CommissionLedgersPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, FINANCE_PERMISSIONS.COMMISSION_LEDGER_READ);
  const canPayOff = hasPermission(permissions, FINANCE_PERMISSIONS.COMMISSION_LEDGER_PAY_OFF);

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有查看提成台账的权限。
      </div>
    );
  }

  const { ledgers, loading, error, fetchLedgers, payOffLedger } = useCommissionLedgers();
  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterStaffId, setFilterStaffId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    void fetchLedgers({
      orderProjectId: filterProjectId || undefined,
      staffId: filterStaffId || undefined,
      status: filterStatus || undefined,
    });
  }, [fetchLedgers, filterProjectId, filterStaffId, filterStatus]);

  async function handlePayOff(ledger: CommissionLedgerResponseDto) {
    if (!confirm(`核销提成 ${fmtCny(ledger.commissionAmount)}？（财务已实际发放）`)) return;
    const result = await payOffLedger(ledger.id, { expectedVersion: ledger.version });
    if (result) {
      void fetchLedgers({
        orderProjectId: filterProjectId || undefined,
        staffId: filterStaffId || undefined,
        status: filterStatus || undefined,
      });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">提成台账</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          placeholder="订单项目 ID 过滤"
          value={filterProjectId}
          onChange={(e) => setFilterProjectId(e.target.value)}
        />
        <input
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          placeholder="员工 ID 过滤"
          value={filterStaffId}
          onChange={(e) => setFilterStaffId(e.target.value)}
        />
        <select
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">加载中…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">项目 ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">角色</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">员工 ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">提成比例</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">提成金额</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {ledgers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted-foreground">暂无记录</td>
                </tr>
              ) : (
                ledgers.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{l.id.slice(0, 8)}…</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{l.orderProjectId.slice(0, 8)}…</td>
                    <td className="px-4 py-3">{ROLE_TYPE_LABELS[l.roleType] ?? l.roleType}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{l.staffId.slice(0, 8)}…</td>
                    <td className="px-4 py-3">{fmtPct(l.commissionRate)}</td>
                    <td className="px-4 py-3 font-semibold">{fmtCny(l.commissionAmount)}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {l.status === 'unlocked' && canPayOff && (
                        <button
                          onClick={() => { void handlePayOff(l); }}
                          className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700"
                        >
                          核销发放
                        </button>
                      )}
                      {l.status === 'paid_off' && (
                        <span className="text-xs text-gray-400">已完成</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
